import { useRef } from 'react';

import { useScrollEdgeFade } from '@/hooks/scroll/use-scroll-edge-fade';
import { cn } from '@/lib/utils';

import { TimeOffBalance } from '../../types/time-off-balance';
import { TimeOffBalanceCard } from './time-off-balance-card';

interface TimeOffBalancesSectionProps {
  balances: TimeOffBalance[];
  /** When provided, each card renders a pencil that calls this with the balance. */
  onEditBalance?: (balance: TimeOffBalance) => void;
}

export const TimeOffBalancesSection = ({
  balances,
  onEditBalance,
}: TimeOffBalancesSectionProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showLeftFade, showRightFade } = useScrollEdgeFade(scrollRef);

  return (
    <div className="relative">
      {/*
        Always a horizontal snap carousel — on small screens one card spans most of the
        viewport (basis-[85%]); from `sm` up cards take a fixed width so several fit in the
        row and any overflow (e.g. many time-off types) scrolls with snapping. The edge
        fades hint at more content on every breakpoint when the row is scrollable.
      */}
      <div
        ref={scrollRef}
        className={cn(
          'flex gap-4 overflow-x-auto pb-1',
          'snap-x snap-mandatory scroll-pl-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
        )}
      >
        {balances.map(balance => (
          <div
            key={balance.type.id}
            className="flex shrink-0 basis-[85%] snap-start sm:basis-[320px]"
          >
            <div className="flex-1">
              <TimeOffBalanceCard
                balance={balance}
                onEdit={onEditBalance ? () => onEditBalance(balance) : undefined}
              />
            </div>
          </div>
        ))}
      </div>

      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-background to-transparent transition-opacity duration-200',
          showLeftFade ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent transition-opacity duration-200',
          showRightFade ? 'opacity-100' : 'opacity-0'
        )}
      />
    </div>
  );
};
